// src/pages/charger/ChargersDriverPage.js
import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  TextField,
  Grid,
  Chip,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Divider,
  CircularProgress
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import EvStationIcon from '@mui/icons-material/EvStation';
import SignalWifi4BarIcon from '@mui/icons-material/SignalWifi4Bar';
import SignalWifiOffIcon from '@mui/icons-material/SignalWifiOff';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import LogoutIcon from '@mui/icons-material/Logout';
import { getCharger } from '../../api/chargerAPI';
import { getCompany } from '../../api/companyAPI';
import { getSite } from '../../api/siteAPI';
import { getChargerSessions } from '../../api/sessionAPI';
import { remoteStartTransaction, remoteStopTransaction } from '../../api/websocketAPI';
import LoadingSpinner from '../../components/common/Loadingspinner';
import ErrorAlert from '../../components/common/ErrorAlert';
import ConnectorStatus from '../../components/connector/ConnectorStatus';
import { useAuth } from '../../contexts/AuthContext';

const ChargersDriverPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setActiveChargerContext, getDriverRFIDId, driverRFID, user, logout } = useAuth();
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Extract parameters from URL
  const chargerId = parseInt(searchParams.get('charger_id') || '0');
  const companyId = parseInt(searchParams.get('company') || '0');
  const siteId = parseInt(searchParams.get('site') || '0');
  const connectorNumber = searchParams.get('connector') || '';

  // Redirect if required parameters are missing
  React.useEffect(() => {
    if (!chargerId || !companyId || !siteId || !connectorNumber) {
      navigate('/');
    }
  }, [chargerId, companyId, siteId, connectorNumber, navigate]);

  // Fetch charger details
  const { data: charger, isLoading, isError, error } = useQuery({
    queryKey: ['charger', chargerId, companyId, siteId],
    queryFn: () => getCharger(chargerId, companyId, siteId),
    enabled: !!chargerId && !!companyId && !!siteId && !isNaN(chargerId) && !isNaN(companyId) && !isNaN(siteId)
  });

  // Fetch company details
  const { data: company } = useQuery({
    queryKey: ['company', companyId],
    queryFn: () => getCompany(companyId),
    enabled: !!companyId && !isNaN(companyId)
  });

  // Fetch site details
  const { data: site } = useQuery({
    queryKey: ['site', siteId],
    queryFn: () => getSite(siteId),
    enabled: !!siteId && !isNaN(siteId)
  });

  // Fetch charger sessions to check for active sessions
  const { data: sessions } = useQuery({
    queryKey: ['charger-sessions', chargerId, companyId, siteId],
    queryFn: () => getChargerSessions(chargerId, companyId, siteId),
    enabled: !!chargerId && !!companyId && !!siteId,
    refetchInterval: 5000, // Refetch every 5 seconds for real-time updates
    refetchIntervalInBackground: true // Continue refetching when tab is not active
  });

  // NEW: Update charger context AFTER all useQuery hooks (moved here)
  React.useEffect(() => {
    if (chargerId && companyId && siteId && connectorNumber) {
      const chargerContext = {
        charger_id: chargerId,
        company_id: companyId,
        site_id: siteId,
        connector_number: connectorNumber,
        charger_name: charger?.ChargerName || `CHARGER_${chargerId}` // Now charger is safely available
      };
      
      setActiveChargerContext(chargerContext);
      console.log('🔌 Updated active charger context:', chargerContext);
    }
  }, [chargerId, companyId, siteId, connectorNumber, charger?.ChargerName, setActiveChargerContext]);

  // Get active sessions for stopping
  const activeSessions = sessions?.filter(
    session => session.ChargerSessionStatus === 'Started'
  ) || [];

  // Debug logging for sessions
  React.useEffect(() => {
    if (sessions) {
      console.log('📊 All sessions:', sessions);
      console.log('🔥 Active sessions (Started):', activeSessions);
      console.log('🔌 Connector number from URL:', connectorNumber);
      
      // Log session status values for debugging
      const uniqueStatuses = [...new Set(sessions.map(s => s.ChargerSessionStatus))];
      console.log('📋 Unique session statuses found:', uniqueStatuses);
    }
  }, [sessions, activeSessions, connectorNumber]);

  // Start transaction mutation
  const startMutation = useMutation({
    mutationFn: () => {
      const driverRFIDId = getDriverRFIDId();
      
      if (!driverRFIDId) {
        throw new Error('Driver RFID card not found. Please contact support.');
      }
      
      console.log('🔑 Starting transaction with RFID:', driverRFIDId);
      
      return remoteStartTransaction(
        charger.ChargerName, 
        driverRFIDId, // Use actual driver RFID instead of hardcoded value
        parseInt(connectorNumber)
      );
    },
    onSuccess: (data) => {
      // Invalidate and refetch sessions to update UI immediately
      queryClient.invalidateQueries({ 
        queryKey: ['charger-sessions', chargerId, companyId, siteId] 
      });
      
      console.log('✅ Session started, refreshing session data');
      
      setSnackbar({
        open: true,
        message: 'Charging session started successfully!',
        severity: 'success'
      });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Failed to start charging session: ${error.message}`,
        severity: 'error'
      });
    }
  });

  // Stop transaction mutation
  const stopMutation = useMutation({
    mutationFn: () => {
      // Find the active session for this connector
      const activeSession = activeSessions.find(
        session => session.ChargerSessionConnectorId === parseInt(connectorNumber)
      );
      
      if (!activeSession) {
        throw new Error('No active session found for this connector');
      }
      
      return remoteStopTransaction(charger.ChargerName, activeSession.ChargeSessionId);
    },
    onSuccess: (data) => {
      // Invalidate and refetch sessions to update UI immediately
      queryClient.invalidateQueries({ 
        queryKey: ['charger-sessions', chargerId, companyId, siteId] 
      });
      
      console.log('🛑 Session stopped, refreshing session data');
      
      setSnackbar({
        open: true,
        message: 'Charging session stopped successfully!',
        severity: 'success'
      });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Failed to stop charging session: ${error.message}`,
        severity: 'error'
      });
    }
  });

  const handleStartSession = async () => {
    startMutation.mutate();
  };

  const handleStopSession = async () => {
    stopMutation.mutate();
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Logout failed: ${error.message}`,
        severity: 'error'
      });
    }
  };

  // Check if there's an active session for this specific connector
  const hasActiveSession = activeSessions.some(
    session => session.ChargerSessionConnectorId === parseInt(connectorNumber)
  );

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isError) {
    return <ErrorAlert message={error?.message || 'Error loading charger information'} />;
  }

  if (!charger) {
    return <ErrorAlert message="Charger not found" />;
  }

  return (
    <Box sx={{ 
      maxWidth: 800, 
      mx: 'auto', 
      p: { xs: 2, sm: 3 }, // Responsive padding: smaller on mobile
      px: { xs: 1, sm: 3 }, // Horizontal padding adjustment for mobile
      minHeight: '100vh', // Ensure full height on mobile
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <Box textAlign="center" mb={{ xs: 3, sm: 4 }}>
        <EvStationIcon sx={{ 
          fontSize: { xs: 40, sm: 48 }, // Smaller icon on mobile
          color: 'primary.main', 
          mb: { xs: 1, sm: 2 } 
        }} />
        <Typography 
          variant="h4" 
          gutterBottom
          sx={{ 
            fontSize: { xs: '1.75rem', sm: '2.125rem' }, // Responsive font size
            fontWeight: { xs: 600, sm: 400 }
          }}
        >
          Charging Station
        </Typography>
        <Typography 
          variant="h6" 
          color="text.secondary"
          sx={{ 
            fontSize: { xs: '1.1rem', sm: '1.25rem' }, // Responsive font size
            px: { xs: 1, sm: 0 } // Add padding on mobile to prevent text cutoff
          }}
        >
          {charger.ChargerName} - Connector {connectorNumber}
        </Typography>
      </Box>

      {/* Charger Status Card */}
      <Card sx={{ mb: { xs: 2, sm: 3 } }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box 
            display="flex" 
            justifyContent="space-between" 
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            flexDirection={{ xs: 'column', sm: 'row' }}
            mb={2}
            gap={{ xs: 2, sm: 0 }}
          >
            <Typography 
              variant="h6"
              sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
            >
              Station Status
            </Typography>
            <Box 
              display="flex" 
              gap={1}
              flexDirection={{ xs: 'row', sm: 'row' }}
              flexWrap="wrap"
            >
              <Chip 
                label={charger.ChargerEnabled ? "Available" : "Unavailable"} 
                color={charger.ChargerEnabled ? "success" : "default"}
                size="small"
              />
              <Chip 
                label={charger.ChargerIsOnline ? "Online" : "Offline"} 
                color={charger.ChargerIsOnline ? "success" : "error"}
                size="small"
                icon={charger.ChargerIsOnline ? <SignalWifi4BarIcon /> : <SignalWifiOffIcon />}
              />
            </Box>
          </Box>
          
          <Divider sx={{ my: { xs: 1.5, sm: 2 } }} />
          
          <Grid container spacing={{ xs: 1, sm: 2 }}>
            <Grid item xs={12} sm={6}>
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
              >
                Location
              </Typography>
              <Typography 
                variant="body1"
                sx={{ 
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  fontWeight: { xs: 500, sm: 400 }
                }}
              >
                {site ? site.SiteName : 'Loading...'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
              >
                Operator
              </Typography>
              <Typography 
                variant="body1"
                sx={{ 
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  fontWeight: { xs: 500, sm: 400 }
                }}
              >
                {company ? company.CompanyName : 'Loading...'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
              >
                Driver Name
              </Typography>
              <Typography 
                variant="body1"
                sx={{ 
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  fontWeight: { xs: 500, sm: 400 },
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <PersonIcon sx={{ mr: 1, fontSize: '1.1rem', color: 'text.secondary' }} />
                {user && (user.first_name || user.last_name) ? 
                  `${user.first_name || ''} ${user.last_name || ''}`.trim() : 
                  'Not provided'
                }
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
              >
                Driver Email
              </Typography>
              <Typography 
                variant="body1"
                sx={{ 
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  fontWeight: { xs: 500, sm: 400 },
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <EmailIcon sx={{ mr: 1, fontSize: '1.1rem', color: 'text.secondary' }} />
                {user?.email || 'Not provided'}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Connector Status Section */}
      <Box sx={{ mb: { xs: 2, sm: 3 } }}>
        <ConnectorStatus
          chargerId={chargerId}
          companyId={companyId}
          siteId={siteId}
          highlightConnector={parseInt(connectorNumber)}
          showTitle={true}
          compact={true}
          maxConnectors={4}
          useStatusEndpoint={false}
        />
      </Box>

      {/* Session Control */}
      <Paper elevation={2} sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Box component="div" noValidate>

          {/* Session Control Buttons */}
          <Box sx={{ 
            display: 'flex', 
            gap: { xs: 1.5, sm: 2 }, 
            justifyContent: 'center',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center'
          }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={startMutation.isPending ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
              onClick={handleStartSession}
              disabled={!charger.ChargerEnabled || !charger.ChargerIsOnline || hasActiveSession || startMutation.isPending || !driverRFID?.RFIDCardEnabled}
              sx={{ 
                minWidth: { xs: '100%', sm: 160 },
                fontSize: { xs: '0.9rem', sm: '1rem' },
                py: { xs: 1.5, sm: 1 }
              }}
            >
              {!driverRFID ? 'No RFID Card' : hasActiveSession ? 'Session Active' : 'Start Session'}
            </Button>
            
            <Button
              variant="outlined"
              color="error"
              size="large"
              startIcon={stopMutation.isPending ? <CircularProgress size={20} color="inherit" /> : <StopIcon />}
              onClick={handleStopSession}
              disabled={!charger.ChargerEnabled || !charger.ChargerIsOnline || !hasActiveSession || stopMutation.isPending}
              sx={{ 
                minWidth: { xs: '100%', sm: 160 },
                fontSize: { xs: '0.9rem', sm: '1rem' },
                py: { xs: 1.5, sm: 1 }
              }}
            >
              Stop Session
            </Button>
            
            <Button
              variant="outlined"
              color="primary"
              size="large"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
              sx={{ 
                minWidth: { xs: '100%', sm: 160 },
                fontSize: { xs: '0.9rem', sm: '1rem' },
                py: { xs: 1.5, sm: 1 }
              }}
            >
              Logout
            </Button>
          </Box>

          {(!charger.ChargerEnabled || !charger.ChargerIsOnline) && (
            <Box sx={{ mt: { xs: 1.5, sm: 2 }, textAlign: 'center', px: { xs: 1, sm: 0 } }}>
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' } }}
              >
                {!charger.ChargerEnabled 
                  ? "This charging station is currently unavailable"
                  : "This charging station is offline"
                }
              </Typography>
            </Box>
          )}

          {!driverRFID && charger.ChargerEnabled && charger.ChargerIsOnline && (
            <Box sx={{ mt: { xs: 1.5, sm: 2 }, textAlign: 'center', px: { xs: 1, sm: 0 } }}>
              <Typography 
                variant="body2" 
                color="error.main"
                sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' } }}
              >
                No RFID card found for your account. Please contact support.
              </Typography>
            </Box>
          )}

          {driverRFID && !driverRFID.RFIDCardEnabled && charger.ChargerEnabled && charger.ChargerIsOnline && (
            <Box sx={{ mt: { xs: 1.5, sm: 2 }, textAlign: 'center', px: { xs: 1, sm: 0 } }}>
              <Typography 
                variant="body2" 
                color="warning.main"
                sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' } }}
              >
                Your RFID card is inactive. Please contact support.
              </Typography>
            </Box>
          )}

          {hasActiveSession && charger.ChargerEnabled && charger.ChargerIsOnline && (
            <Box sx={{ mt: { xs: 1.5, sm: 2 }, textAlign: 'center', px: { xs: 1, sm: 0 } }}>
              <Typography 
                variant="body2" 
                color="success.main"
                sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' } }}
              >
                Charging session is active on this connector
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          '& .MuiSnackbar-root': {
            bottom: { xs: 20, sm: 24 }
          }
        }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{
            fontSize: { xs: '0.85rem', sm: '0.875rem' },
            '& .MuiAlert-message': {
              padding: { xs: '6px 0', sm: '8px 0' }
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ChargersDriverPage;